// @erp/auth-kit/rbac — the RBAC vocabulary + pure claim evaluation.
// Browser-safe: no platform imports, no I/O. Both ends build on this:
// api/rbac() and fe/useCan() call the same can() so UI affordance and server
// enforcement can never disagree on semantics (ADR-001 §14.2 — the API is
// still the only authority; fe checks are sugar).
//
// Source: shippra packages/auth (ported near-verbatim per ADR-003 v1.3 —
// Shippra's 21 roles ONLY; the MBSPSU vocabulary stays deferred reference in
// ADR-002 §6). Control-plane helpers (canAccessControlPlane, auth worker
// URLs) deliberately did not port — the control plane is parked (ADR-002 §12)
// and there is no separate auth worker (auth mounts in erp-api, ADR-002 §6).
import type { Session } from "@erp/core-schemas";

/**
 * EduERP modules a role can belong to. Grows as modules port (LIB → EXAM → …).
 *
 * 'audit' is the one PLATFORM module in this list (ADR-004): db-audit is owned by
 * no business module and mints no roles of its own — it appears here so
 * `audit:<capability>` is a well-formed permission and so MODULE_LABEL can name
 * the module facet the trail console filters on. No RoleMeta names it, so
 * rolesForModule('audit') correctly yields only the module-agnostic operators.
 */
export type ModuleId = "lib" | "lms" | "exam" | "cms" | "fin" | "audit" | "guard";
export const MODULE_IDS: ModuleId[] = ["lib", "lms", "exam", "cms", "fin", "audit", "guard"];
export const MODULE_LABEL: Record<ModuleId, string> = {
  lib: "Library",
  lms: "LMS",
  exam: "Examinations",
  cms: "Content",
  fin: "Finance",
  audit: "Audit trail",
  guard: "Gate desk",
};

export type Role =
  | "platform_admin"
  | "owner" | "admin"                                          // module-agnostic operators
  | "librarian" | "assistant" | "cataloguer" | "data_entry"    // lib operators
  | "patron"                                                   // lib member
  | "accountant"                                               // fin operator
  | "guard"                                                    // gate desk
  | "instructor" | "teaching_assistant"                        // lms operators
  | "student" | "parent"                                       // lms members
  | "coe" | "exam_officer" | "paper_setter" | "moderator"      // exam operators (COE office)
  | "examiner" | "chief_examiner" | "invigilator" | "centre_superintendent"
  | "candidate";                                               // exam member

export interface RoleMeta {
  id: Role;
  label: string;
  /** 'platform' = Shippra staff (install operations); 'operator'/'member' are install-scoped. */
  group: "platform" | "operator" | "member";
  /** Which modules this role belongs to; 'all' = module-agnostic (platform/owner/admin). */
  modules: ModuleId[] | "all";
  description: string;
  /**
   * A role an ordinary administrator must NOT be able to hand out (ADR-007 §5,
   * extended to the role arm).
   *
   * Conferring a ROLE confers everything in it, so an unfenced role assignment
   * routes straight around the pattern fence: an `admin` holding only `core:*`
   * could invite someone as `coe` and manufacture exam capabilities they do not
   * themselves hold. Marking the secrecy roles closes that, while leaving the
   * ordinary appointments (librarian, accountant, instructor) to the
   * administrative capability — because SOMEONE has to be able to hire a
   * librarian, and requiring the hirer to personally hold `lib:*` would make the
   * feature unusable rather than safe.
   *
   * See `canConferRole` for what "restricted" actually costs a conferrer.
   */
  restricted?: boolean;
}

export const ROLES: RoleMeta[] = [
  { id: "platform_admin", label: "Platform Admin", group: "platform", modules: "all", restricted: true, description: "Shippra staff — operates the installation (deploys, migrations, support)." },
  // RESTRICTED, and the reason is the invite path specifically. Appointing is a
  // separate authority from acting (see canConferRole), which is what lets an
  // administrator hire a librarian without personally holding lib:*. Left
  // unrestricted, that same rule would let anyone granted `core:staff` — the
  // capability that gates issuing an invitation — appoint an accomplice as an
  // administrator, and self-invitation being blocked would not stop them. So
  // these two are conferrable only by someone who already holds one of them, or
  // who holds everything they grant.
  { id: "owner", label: "Owner", group: "operator", modules: "all", restricted: true, description: "Account holder — full administrative control across the installation's modules." },
  { id: "admin", label: "Admin", group: "operator", modules: "all", restricted: true, description: "Manages settings, staff and configuration." },
  // ── Gate desk ──
  // The person on the door. Holds ONLY guard capabilities, which is what makes
  // "a guard signs in and sees just their module" fall out of buildNav with no
  // special case: every other module's nav items carry a perm, so they filter
  // away and their modules collapse. The desk grows into visitor and vehicle
  // entries; verification is its first slice.
  { id: "guard", label: "Guard", group: "operator", modules: ["guard"], description: "Gate desk — verifies a scanned credential and records who came in." },
  // ── Finance ──
  // THE finance operator, and the only holder of fin:cash. The duty is physical
  // custody of money plus reconciliation of everything claimed: cash and a DD in
  // hand are entered here and are born paid (the holder IS the check), while a
  // payer-CLAIMED transfer queues and is verified against the bank statement.
  // Deliberately NOT granted to any lib/lms/exam operator: cash custody is a
  // named duty, not a front-desk convenience. Widening later is one data line;
  // narrowing after people hold it is not.
  { id: "accountant", label: "Accountant", group: "operator", modules: ["fin"], description: "Receives cash and demand drafts, and verifies every claimed payment against the institution's bank records." },
  // ── Library ──
  { id: "librarian", label: "Librarian", group: "operator", modules: ["lib"], description: "Day-to-day operations and cataloguing." },
  { id: "assistant", label: "Assistant", group: "operator", modules: ["lib"], description: "Front-desk circulation support." },
  { id: "cataloguer", label: "Cataloguer", group: "operator", modules: ["lib"], description: "Catalogue & acquisitions focus." },
  { id: "data_entry", label: "Data Entry", group: "operator", modules: ["lib"], description: "Record entry, limited scope." },
  { id: "patron", label: "Patron", group: "member", modules: ["lib"], description: "Library member — borrows and reserves." },
  // ── LMS ──
  { id: "instructor", label: "Instructor", group: "operator", modules: ["lms"], description: "Teaches courses — content, assessments, grading and attendance for own sections." },
  { id: "teaching_assistant", label: "Teaching Assistant", group: "operator", modules: ["lms"], description: "Grading and attendance support for assigned sections." },
  { id: "student", label: "Student", group: "member", modules: ["lms"], description: "Learner — enrols in courses, submits work, sees grades." },
  { id: "parent", label: "Parent / Guardian", group: "member", modules: ["lms"], description: "Read-only view of linked learners' progress and announcements." },
  // ── Examinations (COE office; secrecy roles are structurally separated — grants for
  //    award-entry/paper-custody capabilities go ONLY to their specific roles, never to
  //    owner/admin, when the EXAM matrix lands) ──
  { id: "coe", label: "Controller of Examinations", group: "operator", modules: ["exam"], restricted: true, description: "Owns the exam lifecycle — notifications, approvals, results publication, break-glass." },
  { id: "exam_officer", label: "Exam Cell Officer", group: "operator", modules: ["exam"], restricted: true, description: "Registrations, hall tickets, logistics, UFM case files — never enters awards." },
  { id: "paper_setter", label: "Paper Setter", group: "operator", modules: ["exam"], restricted: true, description: "Sets question papers for assigned papers under confidentiality; no candidate/centre data." },
  { id: "moderator", label: "Moderator", group: "operator", modules: ["exam"], restricted: true, description: "Reviews question papers for blueprint/difficulty compliance; never sees candidate identity." },
  { id: "examiner", label: "Examiner", group: "operator", modules: ["exam"], restricted: true, description: "Evaluates dummy-keyed scripts and enters awards for assigned batches only." },
  { id: "chief_examiner", label: "Chief Examiner", group: "operator", modules: ["exam"], restricted: true, description: "Leads a valuation camp — sign-off, third valuation, moderation adjustments." },
  { id: "invigilator", label: "Invigilator", group: "operator", modules: ["exam"], restricted: true, description: "Supervises an exam room — attendance, incident reports." },
  { id: "centre_superintendent", label: "Centre Superintendent", group: "operator", modules: ["exam"], restricted: true, description: "Runs an exam centre — conduct, QP unlock factor, custody." },
  { id: "candidate", label: "Candidate", group: "member", modules: ["exam"], description: "Exam taker — fills forms, downloads hall tickets, views results, applies for revaluation." },
];

const BY_ID = new Map(ROLES.map((r) => [r.id, r]));

export function roleMeta(role: string): RoleMeta | undefined { return BY_ID.get(role as Role); }
export function roleLabel(role: string): string { return BY_ID.get(role as Role)?.label ?? role; }
export function isOperator(role: string): boolean { return BY_ID.get(role as Role)?.group === "operator"; }
export function isPlatformRole(role: string): boolean { return BY_ID.get(role as Role)?.group === "platform"; }

/** Install-facing roles applicable to a module ('all'-module roles included; platform excluded). */
export function rolesForModule(module: ModuleId): RoleMeta[] {
  return ROLES.filter((r) => r.group !== "platform" && (r.modules === "all" || r.modules.includes(module)));
}

/** Roles a visitor may pick when self-registering. Everything else is assigned by an admin. */
export const SIGNUP_ROLES: Role[] = ["owner", "patron", "student", "candidate"];

/** Roles that may administer accounts (the Users directory, role changes). */
export const ADMIN_ROLES: Role[] = ["owner", "admin"];
export function canManageUsers(role: string): boolean { return (ADMIN_ROLES as string[]).includes(role); }

/** Operators land in the admin portal; members go to their module's member surface. */
export function landingFor(role: string): "admin" | "portal" { return isOperator(role) ? "admin" : "portal"; }

export const DEFAULT_ROLE: Role = "patron";

// ── Permissions ────────────────────────────────────────────────────────────
// `<module>:<capability>` strings (ADR-002 §6: 'exam:enter', 'lib:desk', …);
// 'core:' is the module id for install administration. Grant patterns add a
// trailing wildcard ('lib:*') and the full wildcard ('*').

export type Permission = `${string}:${string}`;
export type PermissionPattern = Permission | `${string}:*` | "*";
export type RoleGrants = Partial<Record<Role, readonly PermissionPattern[]>>;

export function parsePermission(perm: Permission): { module: string; capability: string } {
  const i = perm.indexOf(":");
  if (i < 0) return { module: "", capability: perm }; // malformed — never silently drop a char
  return { module: perm.slice(0, i), capability: perm.slice(i + 1) };
}

export function matchesPermission(pattern: PermissionPattern, perm: Permission): boolean {
  if (pattern === "*") return true;
  if (pattern.endsWith(":*")) {
    const prefix = pattern.slice(0, -1); // keep the ':'
    return perm.startsWith(prefix) && perm.length > prefix.length; // 'lib:*' never grants the empty capability 'lib:'
  }
  return pattern === perm;
}

/**
 * The seed grant matrix — MECHANISM-PROVING CORE ONLY. Each module contributes
 * its matrix at its port (LIB first) by passing an extended map into
 * createRbac({ grants }) / SessionProvider — auth-kit never mutates this.
 *
 * Deliberately narrow: owner/admin get install administration ('core:*'), NOT
 * '*' — the exams secrecy separation (paper custody, award entry) must never
 * be reachable via a blanket administrative grant.
 */
export const ROLE_GRANTS: RoleGrants = {
  platform_admin: ["core:*"],
  owner: ["core:*"],
  admin: ["core:*"],
};

/**
 * Key-wise grant composition for module ports: each role's pattern lists are
 * UNIONED (deduped, first-seen order), never replaced. An object spread is the
 * named footgun here — `{...ROLE_GRANTS, ...LIB_GRANTS}` would clobber
 * owner/admin's ['core:*'] with the module's ['lib:*'] and silently 403 every
 * core admin route. Inputs are never mutated; both the gateway's createRbac
 * and the portals' SessionProvider must merge through THIS so the two ends
 * evaluate the same map.
 */
export function mergeGrants(...maps: readonly RoleGrants[]): RoleGrants {
  // Null-prototype accumulator: a crafted '__proto__' role key must become an
  // own property, never a prototype write (same paranoia as can() below).
  const out: Partial<Record<Role, PermissionPattern[]>> = Object.create(null);
  for (const m of maps) {
    for (const role of Object.keys(m) as Role[]) {
      const acc = (out[role] ??= []);
      for (const p of m[role] ?? []) if (!acc.includes(p)) acc.push(p);
    }
  }
  return out;
}

// ── Per-user grants: the layer over the role matrix (ADR-007 §1–§2) ────────
// A role is a STARTING POSITION. The institution then says "this librarian also
// verifies payments" or "this admin does not reconfigure the install" by adding
// one pattern to one person — no new role, no code change, no deploy. Roles stay
// few and meaningful precisely because the exceptions stop being expressed as
// roles.

/** The per-user `{allow, deny}` pair, exactly as it rides on the session. */
export type UserGrants = Session["grants"];

/** What a session with no personal exceptions carries. */
export const NO_USER_GRANTS: UserGrants = { allow: [], deny: [] };

/**
 * The two carriers store grants as plain strings (the JSON projection is
 * `string[]`, because a zod template-literal union would reject a legacy row and
 * fail the whole session shut). The cast is where that widening is repaid: an
 * unparseable pattern is not special-cased, it simply matches nothing —
 * `matchesPermission` returns false for anything that is neither `*`, nor
 * `<m>:*`, nor an exact equal.
 */
const covers = (pattern: string, target: string): boolean =>
  matchesPermission(pattern as PermissionPattern, target as Permission);

const anyCovers = (patterns: readonly string[] | undefined, target: string): boolean =>
  (patterns ?? []).some((p) => covers(p, target));

/**
 * Does any of these roles' matrix patterns cover `target`?
 *
 * Object.hasOwn — a role named 'constructor'/'__proto__' must resolve to "no
 * grants", not to an inherited Object.prototype member (which would crash .some
 * and 500 a route that promised an opaque 403).
 */
const roleCovers = (roles: readonly string[], grants: RoleGrants, target: string): boolean =>
  roles.some((role) => anyCovers(Object.hasOwn(grants, role) ? grants[role as Role] : undefined, target));

/**
 * Pure claim evaluation: is `perm` granted to this session? No I/O, no throw —
 * api/rbac() turns false into an opaque 403, fe/useCan() into a hidden
 * affordance. Both ends call THIS, so the screen and the gateway cannot diverge
 * on semantics.
 *
 * ORDER (ADR-007 §2), and each step is load-bearing:
 *
 *   1. explicit DENY  — wins over everything, checked first and unconditionally.
 *   2. explicit ALLOW — wins over the role matrix.
 *   3. the ROLE matrix.
 *   4. default-deny.
 *
 * Deny-first is what makes a wildcard SUBTRACTABLE, which is the whole reason a
 * deny list exists: owner and admin hold `core:*`, and without a deny arm there
 * is no way to withhold one capability from one administrator short of inventing
 * a narrower role. A deny that could be outvoted by a later allow, by a role, or
 * by a wildcard would not be a control.
 *
 * `session.grants` is REQUIRED in the signature rather than optional. An optional
 * arm reads as harmless and is not: every call site that narrowed to
 * `Pick<Session, "roles">` would silently evaluate with no denies at all, which
 * fails OPEN on the one arm that must never fail open. `Session` gives both arms
 * a zod `.default()`, so a real parsed session always carries the shape and the
 * runtime `??` below only covers hand-built objects.
 */
export function can(
  session: Pick<Session, "roles" | "grants">,
  perm: Permission,
  grants: RoleGrants = ROLE_GRANTS,
): boolean {
  if (anyCovers(session.grants?.deny, perm)) return false;
  if (anyCovers(session.grants?.allow, perm)) return true;
  return roleCovers(session.roles, grants, perm);
}

/**
 * Does `actor` hold `pattern` firmly enough to GIVE IT AWAY? (ADR-007 §5 —
 * nobody grants what they do not hold.)
 *
 * Both sides run through `matchesPermission`, the same function the role scan
 * uses, so a wildcard is conferrable only by a holder of that wildcard: an
 * `accountant` (`fin:read`, `fin:verify`, `fin:cash`) may confer `fin:verify`
 * and may not confer `fin:manage` or `fin:*`.
 *
 * OVERLAP, NOT COVERAGE, ON THE DENY ARM — the difference matters. An actor
 * denied `core:settings` must not be able to confer `core:*`, which would hand
 * someone else the very capability that was taken from them, laundered through a
 * wildcard. A plain `can(actor, "core:*")` would permit exactly that, because a
 * deny of `core:settings` does not match the literal string `core:*`. So the deny
 * arm asks whether the two patterns intersect AT ALL, in either direction.
 *
 * `platform_admin` is not an exception. It holds `core:*`, not `*`, and this rule
 * gives it nothing more — the exam secrecy separation that keeps award entry and
 * paper custody off owner/admin must survive the invitation path exactly as it
 * survives the role matrix.
 */
export function holdsPattern(
  actor: Pick<Session, "roles" | "grants">,
  pattern: string,
  grants: RoleGrants = ROLE_GRANTS,
): boolean {
  // A malformed pattern is never conferrable. It would match nothing anyway, so
  // conferring it is at worst noise on the row — but an invite screen that
  // accepts garbage teaches the operator that garbage is a permission.
  if (pattern !== "*" && !pattern.includes(":")) return false;
  if ((actor.grants?.deny ?? []).some((d) => covers(d, pattern) || covers(pattern, d))) return false;
  if (anyCovers(actor.grants?.allow, pattern)) return true;
  return roleCovers(actor.roles, grants, pattern);
}

export interface ConferralCheck {
  ok: boolean;
  /** The patterns the actor does not hold. Empty iff `ok`. */
  refused: string[];
}

/**
 * Check a whole conferral. REFUSE THE REQUEST, NEVER TRIM IT (ADR-007 §5):
 * silently dropping the patterns the actor could not confer produces an invite
 * that looks accepted and grants less than the screen said, which is how a
 * support ticket becomes a security incident. The caller reports `refused` to the
 * operator — this is an authoring mistake by a signed-in staff member, not an
 * anonymous probe, so naming the patterns is help, not an oracle.
 *
 * ONLY THE ALLOW ARM IS FENCED. Withholding a capability is not an escalation, so
 * an actor may deny anything they can name; conferring is what is checked.
 */
export function canConfer(
  actor: Pick<Session, "roles" | "grants">,
  allow: readonly string[],
  grants: RoleGrants = ROLE_GRANTS,
): ConferralCheck {
  const refused = allow.filter((p) => !holdsPattern(actor, p, grants));
  return { ok: refused.length === 0, refused };
}

/**
 * May `actor` appoint someone to `role`?
 *
 * The role arm of §5, and it deliberately does NOT read "the actor must hold
 * everything the role grants". Under the composed matrix an `admin` holds
 * `core:*` and nothing else, so that rule would forbid appointing a librarian —
 * the feature would be safe and useless. Someone has to be able to hire a
 * librarian, and the control on that is the administrative capability the route
 * already checks plus an audit row naming who did it. That is how real systems
 * work: appointing is a separate authority from acting, deliberately, so the
 * person who appoints is not the person who acts.
 *
 * WHAT IS FENCED is the `restricted` set — the roles where conferral WOULD be a
 * capability laundry. Three answers, in order:
 *
 *   • an unknown role is never conferrable (a garbage role string on a user row
 *     is a support incident, and it costs nothing to refuse it here);
 *   • an unrestricted role is conferrable;
 *   • a restricted role is conferrable by someone who already HOLDS it — a COE
 *     appoints a COE — or by someone who personally holds every pattern it
 *     grants.
 *
 * A restricted role the matrix does not describe yet is REFUSED, not allowed.
 * The exam matrix has not landed, so `ROLE_GRANTS.coe` is undefined; reading an
 * absent pattern list as "this role grants nothing, so handing it out is
 * harmless" would leave every secrecy role freely conferrable for exactly as
 * long as the module takes to port — which is the window an attacker would want.
 *
 * SELF-APPOINTMENT IS NOT CLOSED BY THIS FUNCTION and cannot be: an
 * unrestricted role is conferrable, and an actor controls their own inbox. The
 * invite path refuses an invitation addressed to the actor's own account for
 * that reason; two colluding administrators remain outside what any permission
 * system can answer, which is what the audit trail is for.
 */
export function canConferRole(
  actor: Pick<Session, "roles" | "grants">,
  role: string,
  grants: RoleGrants = ROLE_GRANTS,
): ConferralCheck {
  const meta = roleMeta(role);
  if (!meta) return { ok: false, refused: [role] };
  if (!meta.restricted) return { ok: true, refused: [] };
  if (actor.roles.includes(role)) return { ok: true, refused: [] };
  // A PLATFORM role is Shippra staff, not an install appointment, so the pattern
  // fallback below is skipped for it. platform_admin's grant IS `core:*` — which
  // every owner and admin already holds — so without this line the fallback
  // would find the patterns "covered" and hand any administrator the ability to
  // mint Shippra staff. Capability-wise that is not an escalation; it is still
  // the wrong answer, because the role means something the capabilities do not
  // say. (A platform_admin conferring platform_admin passed on the line above.)
  if (meta.group === "platform") return { ok: false, refused: [role] };
  const patterns = Object.hasOwn(grants, role) ? (grants[role as Role] ?? []) : [];
  if (patterns.length === 0) return { ok: false, refused: [role] };
  const check = canConfer(actor, patterns, grants);
  // Report the ROLE, not the patterns inside it. "You cannot grant exam:enter"
  // is a confusing answer to "invite this person as an examiner".
  return check.ok ? check : { ok: false, refused: [role] };
}

/** The roles this actor may hand out — what an invite screen's picker should show. */
export function conferrableRoles(
  actor: Pick<Session, "roles" | "grants">,
  grants: RoleGrants = ROLE_GRANTS,
): RoleMeta[] {
  return ROLES.filter((r) => canConferRole(actor, r.id, grants).ok);
}
